import { ConfigProvider as AntdConfigProvider } from 'antd';
import enUS from 'antd/es/locale/en_US';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { TranslatorProvider } from 'react-translate-plus';

import { getTranslations } from '../api/localisation/getTranslations';
import { setCurrentLocale } from './axiosConfig';
import fallback from './fallback-localisation.json';
import { SupportedLocale } from './locale';

export interface LocalisationDictionary {
  [key: string]: string | Record<string, string>;
}

export type TranslationFunction = (key: string, params?: any) => string;

export interface LocalisationContextProps {
  changeLocale: (locale: SupportedLocale) => void;
  currentLocale: SupportedLocale;
  currentLanguage: string;
  currentCountry: string;
}

const LocalisationContext = createContext<LocalisationContextProps | null>(null);
const initialTranslation = Object.freeze({
  locale: SupportedLocale.default,
  ...Object.freeze(fallback),
});

const applyTranslations = (
  nextLocale: SupportedLocale,
  newTranslations: LocalisationDictionary,
) => {
  const translations = { ...initialTranslation, locale: nextLocale };
  Object.entries(newTranslations).forEach(([groupKey, groupValue]) => {
    Object.entries(groupValue).forEach(([itemKey, itemValue]) => {
      if (translations[groupKey]) {
        translations[groupKey][itemKey] = itemValue;
      }
    });
  });
  return translations;
};

interface Props {
  initialLocale: SupportedLocale;
}

export const LocalisationContextProvider: React.FC<PropsWithChildren<Props>> = ({
  initialLocale,
  children,
}) => {
  const [translations, setTranslations] = useState<LocalisationDictionary>(initialTranslation);

  const changeLocale = useCallback((nextLocale: SupportedLocale) => {
    const fetch = async () => {
      const data = await getTranslations(nextLocale);
      if (!data) {
        console.error(`Localisation for ${nextLocale} is not found.`);
        return;
      }
      const updatedTranslations = applyTranslations(nextLocale, data);
      setTranslations(updatedTranslations);
      setCurrentLocale(nextLocale);
    };
    fetch();
  }, []);

  useEffect(() => {
    changeLocale(initialLocale);
  }, [changeLocale, initialLocale]);

  const locale = translations.locale as SupportedLocale;
  const [language, country] = locale.split('-');
  setCurrentLocale(initialLocale);

  useEffect(() => {
    if (!locale) {
      return;
    }
    const [lang] = locale.split('-');
    document.documentElement.setAttribute('lang', lang);
  }, [locale]);

  return (
    <LocalisationContext.Provider
      value={{
        changeLocale,
        currentLocale: locale,
        currentLanguage: language,
        currentCountry: country,
      }}
    >
      <AntdConfigProvider locale={enUS}>
        <TranslatorProvider translations={translations}>{children}</TranslatorProvider>
      </AntdConfigProvider>
    </LocalisationContext.Provider>
  );
};

/*
  Usage example (copy-paste e.g. into Navigation.tsx):

  import { useTranslate } from 'react-translate-plus';

  const Navigation = () => {
    const t = useTranslate('contactForm');
    const { changeLocale, currentLocale } = useLocalisation();
    console.log(`contactForm.title = "${t('title')}", locale: ${currentLocale}`);

    useEffect(() => {
      setTimeout(() => {
        changeLocale('en-GB'); // triggers API call
      }, 3000);
    }, [changeLocale]);
    ...
*/
export const useLocalisation = (): LocalisationContextProps => {
  const ctx = useContext(LocalisationContext);
  if (!ctx) {
    throw new Error('Please configure LocalisationContext before using "useLocalisation()"');
  }
  return ctx;
};
